import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { parseISO, format } from 'date-fns'
import { SearchBox, withSearch } from '@elastic/react-search-ui'
import { Image, StyledText } from '../..'
import SearchIcon from '../../icons/SearchIcon'
import { SEARCH_RESULT } from '../../../utils/types'
import getSearchImageObj from '../getSearchImgObj'
import {
  autocompleteStyles,
  searchboxStyles,
} from './styles'
import { HOST } from '../../../utils/env'
import { categoryListStyles } from '../styles'
import processResults from '../processResults'

export const STVAutocompleteView = ({ autocompletedResults, getItemProps, inputValue }) => {
  const processedResults = processResults(autocompletedResults).slice(0, 10)
  const listRef = useRef(null)
  const prevInputValueRef = useRef(inputValue)
  const highlightTriggeredRef = useRef(false)

  useEffect(() => {
    let cleanup = () => {}

    if (inputValue !== prevInputValueRef.current) {
      highlightTriggeredRef.current = false
    }

    if (processedResults.length > 0 && !highlightTriggeredRef.current) {
      const firstItemProps = getItemProps({ item: processedResults[0], index: 0 })
      if (firstItemProps.onMouseMove) {
        const timer = setTimeout(() => {
          if (listRef.current && listRef.current.children[0]) {
            firstItemProps.onMouseMove({})
            highlightTriggeredRef.current = true
          }
        }, 0)
        cleanup = () => clearTimeout(timer)
      } else {
        highlightTriggeredRef.current = true
      }
    }

    prevInputValueRef.current = inputValue

    return cleanup
  }, [processedResults, getItemProps, inputValue])

  return (
    <div className="autocomplete-wrapper">
      <ul className="autocomplete-container" ref={listRef}>
        {processedResults.length > 0
          ? (
            processedResults.map((result, i) => (
              <li
                className="autocomplete-result"
                {...getItemProps({
                  item: result,
                  index: i,
                  key: result?.id?.raw,
                })}
              >
                <div className="autocomplete-result-image">
                  <Image image={getSearchImageObj(result)} sizes="99x56" />
                </div>
                <div className="autocomplete-result-content">
                  <StyledText className="display5" style={{ fontWeight: 600 }}>
                    {result?.title?.raw}
                  </StyledText>
                  {
                    (Array.isArray(result?.category?.raw) && result?.category?.raw?.length > 0) && (
                      <ul className="category-list">
                        {
                          result.category.raw.map(
                            category => (
                              <StyledText
                                className="cardLabel"
                                el="li"
                                style={{ fontWeight: 600 }}
                                key={category}
                              >
                                {category}
                              </StyledText>

                            )
                          )
                        }
                      </ul>
                    )
                  }
                  {
                    typeof result?.category?.raw === 'string' && (
                      <ul className="category-list">
                        <StyledText className="cardLabel" el="li" style={{ fontWeight: 600 }}>
                          {result.category.raw}
                        </StyledText>
                        {
                          result.category.raw === 'TV Guide' && (
                            <StyledText className="cardLabel" el="li" style={{ fontWeight: 600 }}>
                              {format(parseISO(result?.tvGuideDate?.raw), 'do MMMM yyyy')}
                            </StyledText>
                          )
                        }
                      </ul>
                    )
                  }
                </div>
              </li>
            ))
          )
          : null}
        <style jsx>{categoryListStyles}</style>
        <style jsx>{autocompleteStyles}</style>
      </ul>
    </div>
  )
}

STVAutocompleteView.propTypes = {
  autocompletedResults: PropTypes.arrayOf(SEARCH_RESULT).isRequired,
  getItemProps: PropTypes.func.isRequired,
  inputValue: PropTypes.string,
}

STVAutocompleteView.defaultProps = {
  inputValue: '',
}

export const STVInputView = ({
  getAutocomplete,
  getInputProps,
  autocompletedResults, // Add autocompletedResults prop
  searchTerm, // Add searchTerm prop
}) => {
  const inputElement = useRef(null)
  // Show message if user typed something AND there are no autocomplete results
  const showNoResults = searchTerm && autocompletedResults && autocompletedResults.length === 0
  useEffect(() => {
    inputElement.current.focus()
  }, [])

  const inputProps = getInputProps({
    placeholder: 'Search by show or movie name',
  })
  const {
    onKeyDown: originalOnKeyDown,
    onBlur: originalOnBlur,
    ...restInputProps
  } = inputProps

  return (
    <div className="sui-search-box__wrapper st-default-search-input-wrap">
      <SearchIcon width={22} height={22} />
      <input
        ref={inputElement}
        id="search-text"
        {...restInputProps}
        className={`searchbox-input st-default-search-input ${restInputProps.className || ''}`}
        onKeyDown={(event) => {
          if (event.key === 'Tab') {
            // Simulate ArrowUp for Shift+Tab
            if (event.shiftKey) {
              event.preventDefault() // Prevent default reverse focus
              if (originalOnKeyDown) {
                // Minimal fake event for ArrowUp
                const fakeEvent = {
                  key: 'ArrowUp',
                  code: 'ArrowUp',
                  preventDefault: () => event.preventDefault(),
                }
                originalOnKeyDown(fakeEvent)
              }
              return
            }

            event.preventDefault()
            if (originalOnKeyDown) {
              const fakeEvent = {
                key: 'ArrowDown',
                code: 'ArrowDown',
                preventDefault: () => event.preventDefault(),
              }
              originalOnKeyDown(fakeEvent)
            }
          } else if (originalOnKeyDown) {
            originalOnKeyDown(event)
          }
        }}
        onBlur={(event) => {
          // Only call the original blur handler (which closes the dropdown)
          // if the input is empty.
          if (!inputElement.current || !inputElement.current.value) {
            if (originalOnBlur) {
              originalOnBlur(event)
            }
          }
        }}
      />
      {getAutocomplete()}
      {showNoResults && (
        <div className="autocomplete-wrapper">
          <ul className="autocomplete-container">
            <li className="autocomplete-no-results">
              <StyledText className="display5" style={{ padding: '10px 15px', color: 'white' }}>
                No results found
              </StyledText>
            </li>
            {/* Styles are applied globally via imported autocompleteStyles */}
          </ul>
        </div>
      )}
      <style jsx>{autocompleteStyles}</style>
      <style jsx>{searchboxStyles}</style>
    </div>
  )
}

STVInputView.propTypes = {
  getAutocomplete: PropTypes.func.isRequired,
  getInputProps: PropTypes.func.isRequired,
  autocompletedResults: PropTypes.arrayOf(PropTypes.object),
  searchTerm: PropTypes.string,
}

STVInputView.defaultProps = {
  autocompletedResults: [],
  searchTerm: '',
}

const STVSearchboxComponent = ({
  autocompletedResults,
  searchTerm,
}) => (
  <SearchBox
    inputView={({
      getAutocomplete,
      getInputProps,
    }) => STVInputView({
      getAutocomplete,
      getInputProps,
      autocompletedResults,
      searchTerm,
    })}
    autocompleteResults={{
      titleField: 'title',
      urlField: 'url',
    }}
    autocompleteView={
      ({
        autocompletedResults: resultsForView,
        getItemProps,
        inputValue,
      }) => STVAutocompleteView({ autocompletedResults: resultsForView, getItemProps, inputValue })
    }
    onSubmit={(e) => {
      const searchText = e

      if (searchText != null && searchText !== '') {
        if (String(window.location).includes(`https://www.${HOST}`)) {
          window.location = `//player.${HOST}/search?q=${searchText}`
        } else {
          window.location = `/search?q=${searchText}`
        }
      }
    }}
  />
)
STVSearchboxComponent.propTypes = {
  autocompletedResults: PropTypes.arrayOf(PropTypes.object),
  searchTerm: PropTypes.string,
}

STVSearchboxComponent.defaultProps = {
  autocompletedResults: [],
  searchTerm: '',
}

const STVSearchbox = withSearch(({ autocompletedResults, searchTerm }) => ({
  autocompletedResults,
  searchTerm,
}))(STVSearchboxComponent)

export default STVSearchbox
